<template>
    <div slot="content" class="tile">
        <template v-for="(action, actionIndex) in actions">
            <div class="tile-content" :key="`tile-${action.$internalId}`">
                <div class="grid-content grid-unstackable align-items-center">
                    <div class="grid-item cursor-pointer" @click="$emit('toggle-action-status', actionIndex)">
                        <h2>{{action.name | unKebabCase}}</h2>
                    </div>
                    <div class="grid-item no-flex">
                        <v-toggle
                            @input="$emit('toggle-action-status', actionIndex)"
                            :value="true"
                            :selected="action.status"/>
                    </div>
                    <div class="grid-item no-flex">
                        <v-icon-button
                            type="basic"
                            size="small"
                            @click="confirmDelete(action, actionIndex)"
                            tooltip="Delete action">
                            <v-icon icon="close"/>
                        </v-icon-button>
                    </div>
                </div>
            </div>
            <component :key="`component-${action.$internalId}`"
                v-if="action.status"
                :is="getActionComponent(action)"
                :is-form-updating="isFormUpdating"
                :system-event-type="event.eventType"
                :new-action-predicate="newActionPredicate"
                :pre-processor="preProcessor"
                @input="updateAction($event, actionIndex)"
                :validation-aggregator="validationAggregator"
                :action="action"/>
        </template>
        <div class="tile-content">
            <div class="grid-content">
                <div class="grid-item">
                    <v-popper position="bottomStart" ref="csvUpload" fluid>
                        <div slot="trigger">
                            <r-button v-popper-trigger fluid>
                                <span v-if="hasNoActions">Add action</span>
                                <span v-else>Add another action</span>
                            </r-button>
                        </div>
                        <div class="popover popover" slot="content">
                            <div class="popover-control">
                                <div class="popover-content popover-menu">
                                    <a
                                        v-for="item in actionOptions"
                                        :key="item.value"
                                        class="popover-menu-item"
                                        href="#"
                                        @click.prevent="$emit('add-action', item.value)">
                                        {{item.label}}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </v-popper>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import EventModel from '@/common/models/rules/event-model';
    import vPopper from '@/components/v-popper';
    import {
        vIcon, vIconButton, vToggle,
    } from '@/components/form';
    import {createDialog, vConfirm} from '@/components/modal/dialogs';
    import _ from '@/common/helpers';
    import RuleActionModel from '@/common/models/rules/actions/rule-action-model';
    import AddNsfFee from '@/modules/rules-engine/actions/add-nsf-fee/add-nsf-fee';
    import AddRiskScore from '@/modules/rules-engine/actions/add-risk-score/add-risk-score';
    import Blacklist from '@/modules/rules-engine/actions/blacklist/blacklist';
    import BlankAction from '@/modules/rules-engine/actions/blank-action';
    import SendEmail from '@/modules/rules-engine/actions/send-email/send-email';
    import StopSubscriptions from '@/modules/rules-engine/actions/stop-subscriptions/stop-subscriptions';
    import ChurnSubscription from '@/modules/rules-engine/actions/churn-subscription/churn-subscription';
    import RequestKYC from '@/modules/rules-engine/actions/request-kyc/request-kyc';
    import GuessPaymentCardExpiration
        from '@/modules/rules-engine/actions/guess-payment-card-expiration/guess-payment-card-expiration';
    import CancelScheduledPayments
        from '@/modules/rules-engine/actions/cancel-scheduled-payments/cancel-scheduled-payments';
    import PickGatewayAccount from '@/modules/rules-engine/actions/pick-gateway-account/pick-gateway-account';
    import SchedulePaymentRetry from '@/modules/rules-engine/actions/schedule-payment-retry/schedule-payment-retry';
    import SchedulePayment from '@/modules/rules-engine/actions/schedule-payment/schedule-payment';
    import TriggerWebhook from '@/modules/rules-engine/actions/trigger-webhook/trigger-webhook';
    import OfferPurchaseBump from '@/modules/rules-engine/actions/offer-purchase-bump/offer-purchase-bump';
    import ShowDescriptorDisclaimer
        from '@/modules/rules-engine/actions/show-descriptor-disclaimer/show-descriptor-disclaimer';
    import ScheduleInvoiceRetry
        from '@/modules/rules-engine/actions/schedule-invoice-retry/schedule-invoice-retry';
    import TagOrUntagCustomer from '@/modules/rules-engine/actions/tag-or-untag-customer/tag-or-untag-customer';
    import DisplayOtherChoices from '@/modules/rules-engine/actions/display-other-choices/display-other-choices';
    import ScheduleReminder from '@/modules/rules-engine/actions/schedule-reminder/schedule-reminder';
    import ResetReminder from '@/modules/rules-engine/actions/reset-reminder/reset-reminder';
    import RemoveReminder from '@/modules/rules-engine/actions/remove-reminder/remove-reminder';


    export default {
        name: 'actions-list',
        components: {
            vPopper, vToggle, vIcon, vIconButton,
        },
        props: {
            actions: Array,
            validationAggregator: Object,
            isFormUpdating: Boolean,
            preProcessor: Object,
            newActionPredicate: Function,
            event: {
                type: EventModel,
            },
        },
        computed: {
            actionOptions() {
                return this.event.availableActions.map(name => ({
                    label: _.unKebabCase(name),
                    value: name,
                }));
            },
            hasNoActions() {
                return this.actions.length === 0;
            },
        },
        methods: {
            async confirmDelete(action, actionIndex) {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: `Delete ${_.unKebabCase(action.name)}`,
                    content: `Are you sure you want to delete this action? This cannot be undone.`,
                    confirmLabel: 'Delete',
                    dangerConfirm: true,
                };
                if (await confirmDialog(dialogParams)) {
                    this.$emit('delete-action', actionIndex);
                }
            },
            getActionComponent(action) {
                switch (action.name) {
                    case RuleActionModel.Names.addRiskScore:
                        return AddRiskScore;
                    case RuleActionModel.Names.blacklist:
                        return Blacklist;
                    case RuleActionModel.Names.guessPaymentCardExpiration:
                        return GuessPaymentCardExpiration;
                    case RuleActionModel.Names.pickGatewayAccount:
                        return PickGatewayAccount;
                    case RuleActionModel.Names.requestKyc:
                        return RequestKYC;
                    case RuleActionModel.Names.sendEmail:
                        return SendEmail;
                    case RuleActionModel.Names.schedulePayment:
                        return SchedulePayment;
                    case RuleActionModel.Names.schedulePaymentRetry:
                        return SchedulePaymentRetry;
                    case RuleActionModel.Names.triggerWebhook:
                        return TriggerWebhook;
                    case RuleActionModel.Names.stopSubscriptions:
                        return StopSubscriptions;
                    case RuleActionModel.Names.churnSubscription:
                        return ChurnSubscription;
                    case RuleActionModel.Names.cancelScheduledPayments:
                        return CancelScheduledPayments;
                    case RuleActionModel.Names.addNsfFee:
                        return AddNsfFee;
                    case RuleActionModel.Names.showDescriptorDisclaimer:
                        return ShowDescriptorDisclaimer;
                    case RuleActionModel.Names.offerPurchaseBump:
                        return OfferPurchaseBump;
                    case RuleActionModel.Names.scheduleInvoiceRetry:
                        return ScheduleInvoiceRetry;
                    case RuleActionModel.Names.tagOrUntagCustomer:
                        return TagOrUntagCustomer;
                    case RuleActionModel.Names.displayOtherChoices:
                        return DisplayOtherChoices;
                    case RuleActionModel.Names.scheduleReminder:
                        return ScheduleReminder;
                    case RuleActionModel.Names.resetReminder:
                        return ResetReminder;
                    case RuleActionModel.Names.removeReminder:
                        return RemoveReminder;
                    default:
                        return BlankAction;
                }
            },
            updateAction(action, index) {
                this.$emit('update-action', {action, index});
            },
        },
    };
</script>
